import React, { useRef, useState } from "react";
import { rem } from "polished";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import CoCartAPI from "@cocart/cocart-rest-api";
import ArrowLeft from "../../assets/img/arrow-left.png";
import { ReactComponent as RoyalTubsLogo } from "../../assets/img/royaltubs-logo.svg";
import mq from "../../helpers/media-queries";
import DetailsModal from "../DetailsModal/DetailsModal";
import RenderContainer from "../RenderContainer/RenderContainer";
import { SketchfabProvider } from "../Sketchfab/SketchfabContext";
import Breadcrumbs from "./Model/Breadcrumbs";
import Slider from "./Model/Slider";
import Popup from "./Model/Popup";
import Gallery from "./Model/Gallery";
import Headline from "./Model/HeadlineWithDiscount";
import PopupTabs from "./Model/PopupTabs";
import Sidebar from "./Model/Sidebar";
import { ConfigurationProvider } from "./Wooden/ConfigurationContext";
import RenderIframe from "./Wooden/RenderIframe";

const Container = styled.div`
    ${mq("sm", "min")} {
        height: 100%;
        display: flex;
        cursor: pointer;
    }
`;

const LogoContainer = styled.div`
    display: none;

    ${mq("desktop", "min")} {
        position: absolute;
        width: 150px;
        left: 50%;
        transform: translateX(-50%);
        top: 20px;
        display: block;
    }

    svg {
        width: 100%;
    }
`;

const Back = styled.a`
    font-weight: 400;
    font-size: 12px;
    font-family: sans-serif;
    color: #5a4a44;
    text-decoration: none;
    position: absolute;
    top: 27px;
    left: 42px;
    display: flex;

    img {
        height: 12px;
        margin-right: ${rem(8)};
    }
`;

const accesoriesReducer = (acc, current) => ({
    ...acc,
    [current.name]: current.label,
});

const ModelPage = ({
    headlineData,
    breadcrumbData,
    details,
    galleryData,
    specs,
    linerColor,
    keyFeaturesData,
    wooId,
    modelUID,
    backToLabel,
    videoUrl,
    qrCode,
    buttonLink,
    customData,
}) => {
    const { pathname } = useLocation();
    const [, path] = pathname.split("/");
    const backToLink = `/${path}`;
    const history = useHistory();
    const viewerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [openSlide, setOpenSlide] = useState(0);
    const [color, setColor] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);
    const [initialSlide, setInitialSlide] = useState(0);
    const [modelId, setModelId] = useState(modelUID);
    const [activeAccessories, setActiveAccessories] = useState([]);
    const price = activeAccessories.reduce(
        (currentTotal, accesory) => accesory.price * accesory.quantity + currentTotal,
        details.base.price,
    );

    const CoCart = new CoCartAPI({
        url: "https://royaltubs.co.uk",
        version: "cocart/v2",
    });

    const handleAddToCart = async () => {
        const id = String(wooId);
        const accessories = activeAccessories.reduce(accesoriesReducer, {});
        const requiredData = { ...customData, ...accessories };

        try {
            const response = await CoCart.post("cart/add-item", {
                id,
                quantity: "1",
                item_data: {
                    nyp: price,
                    custom_data: linerColor ? { ...requiredData, Color: linerColor[color].label } : requiredData,
                },
            });

            // Successful request
            console.log({ response });

            const { cart_key } = response.data;

            window.location.href = `https://royaltubs.co.uk/cart/?nocache&cocart-load-cart=${cart_key}`;
        } catch (error) {
            // Invalid request, for 4xx and 5xx statuses
            console.error(error);
        }
    };

    return (
        <ConfigurationProvider>
            <Popup
                open={popupOpen}
                setOpen={setPopupOpen}
                data={galleryData.details.items}
                modelId={modelId}
                initialSlide={initialSlide}
            />

            <SketchfabProvider modelUID={modelId} viewerRef={viewerRef}>
                <Container>
                    <Breadcrumbs links={breadcrumbData} showMobileOnly />
                    <Headline data={headlineData} showMobileOnly show="yes" />
                    <Slider
                        data={galleryData.details.items}
                        setPopupOpen={setPopupOpen}
                        setInitialSlide={setInitialSlide}
                        buttonLink={buttonLink}
                        color={color}
                    />

                    <RenderContainer relative hideMobile>
                        <Gallery
                            data={galleryData}
                            setIsOpen={setIsOpen}
                            isOpen={isOpen}
                            setOpenSlide={setOpenSlide}
                            color={color}
                        />
                        <DetailsModal
                            {...galleryData.details}
                            open={isOpen}
                            modalToggle={() => setIsOpen(!isOpen)}
                            openSlide={openSlide}
                        />
                        <PopupTabs videoUrl={videoUrl} qrCode={qrCode} />
                        <Back onClick={() => history.push(backToLink)}>
                            <img src={ArrowLeft} alt="" /> Back to {backToLabel}
                        </Back>
                        <LogoContainer>
                            <a href="/">
                                <RoyalTubsLogo />
                            </a>
                        </LogoContainer>
                        <RenderIframe viewerRef={viewerRef} />
                    </RenderContainer>
                    <Sidebar
                        popupOpen={isOpen}
                        setPopupOpen={setIsOpen}
                        color={color}
                        setColor={setColor}
                        handleAddToCart={handleAddToCart}
                        details={details}
                        linerColor={linerColor}
                        specs={specs}
                        keyFeaturesData={keyFeaturesData}
                        headlineData={headlineData}
                        breadcrumbData={breadcrumbData}
                        activeAccessories={activeAccessories}
                        setActiveAccessories={setActiveAccessories}
                        price={price}
                        setModelId={setModelId}
                    />
                </Container>
            </SketchfabProvider>
        </ConfigurationProvider>
    );
};

export default ModelPage;
